import type {
  Tour,
  TourListResponse,
  TourRequest,
  TourCreatePayload,
  TourUpdatePayload,
  TourErrorResponse,
  TourStats,
  TourOutcomeSummary,
} from '../Types/Tours.types';
import { authenticatedFetch } from '@/Auth/services/auth.service';
import { env } from '@/config/env';

const TOURS_ENDPOINT = '/v1/tours';

export const getTours = async (params: TourRequest = {}): Promise<TourListResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.pageId) queryParams.append('pageId', params.pageId);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.tenantSlug) queryParams.append('tenantSlug', params.tenantSlug);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const url = `${env.bffBaseURL}${TOURS_ENDPOINT}${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: TourErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch tours',
        statusCode: response.status,
      }));
      if (response.status === 403) throw new Error('You do not have permission to view tours');
      throw new Error(errorData.message || 'Failed to fetch tours');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred');
  }
};

export const getTourById = async (id: string): Promise<Tour> => {
  try {
    const url = `${env.bffBaseURL}${TOURS_ENDPOINT}/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: TourErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch tour',
        statusCode: response.status,
      }));
      if (response.status === 403) throw new Error('You do not have permission to view this tour');
      if (response.status === 404) throw new Error('Tour not found');
      throw new Error(errorData.message || 'Failed to fetch tour');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred');
  }
};

export const createTour = async (payload: TourCreatePayload): Promise<Tour> => {
  try {
    const url = `${env.bffBaseURL}${TOURS_ENDPOINT}`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: TourErrorResponse = await response.json().catch(() => ({
        message: 'Failed to create tour',
        statusCode: response.status,
      }));
      if (response.status === 403) throw new Error('You do not have permission to create tours');
      if (response.status === 409)
        throw new Error('A tour with this version already exists for this page');
      throw new Error(errorData.message || 'Failed to create tour');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred while creating tour');
  }
};

export const updateTour = async (id: string, payload: TourUpdatePayload): Promise<Tour> => {
  try {
    const url = `${env.bffBaseURL}${TOURS_ENDPOINT}/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: TourErrorResponse = await response.json().catch(() => ({
        message: 'Failed to update tour',
        statusCode: response.status,
      }));
      if (response.status === 400) throw new Error(errorData.message || 'Failed to update tour');
      if (response.status === 403)
        throw new Error('You do not have permission to update this tour');
      if (response.status === 404) throw new Error('Tour not found');
      if (response.status === 409)
        throw new Error('A tour with this version already exists for this page');
      throw new Error(errorData.message || 'Failed to update tour');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred while updating tour');
  }
};

export const bumpTourVersion = async (id: string): Promise<Tour> => {
  try {
    const url = `${env.bffBaseURL}${TOURS_ENDPOINT}/${id}/bump-version`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });

    if (!response.ok) {
      const errorData: TourErrorResponse = await response.json().catch(() => ({
        message: 'Failed to bump tour version',
        statusCode: response.status,
      }));
      if (response.status === 403)
        throw new Error('You do not have permission to update this tour');
      if (response.status === 404) throw new Error('Tour not found');
      throw new Error(errorData.message || 'Failed to bump tour version');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred while bumping tour version');
  }
};

export const deleteTour = async (id: string): Promise<void> => {
  try {
    const url = `${env.bffBaseURL}${TOURS_ENDPOINT}/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: TourErrorResponse = await response.json().catch(() => ({
        message: 'Failed to delete tour',
        statusCode: response.status,
      }));
      if (response.status === 403)
        throw new Error('You do not have permission to delete this tour');
      if (response.status === 404) throw new Error('Tour not found');
      throw new Error(errorData.message || 'Failed to delete tour');
    }
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred while deleting tour');
  }
};

/**
 * How every tour is going, in one request.
 *
 * For the overview screen. Asking each tour's own `/stats` would be one request per tour.
 */
export const getTourOutcomeSummary = async (): Promise<TourOutcomeSummary> => {
  const response = await authenticatedFetch(`${env.bffBaseURL}${TOURS_ENDPOINT}/stats/summary`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Não foi possível carregar o desfecho dos tours');
  }

  return (await response.json()) as TourOutcomeSummary;
};

export const getTourStats = async (id: string): Promise<TourStats> => {
  try {
    const url = `${env.bffBaseURL}${TOURS_ENDPOINT}/${id}/stats`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: TourErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch tour stats',
        statusCode: response.status,
      }));
      if (response.status === 403) throw new Error('You do not have permission to view tour stats');
      if (response.status === 404) throw new Error('Tour not found');
      throw new Error(errorData.message || 'Failed to fetch tour stats');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred while fetching tour stats');
  }
};

/**
 * Forget that one person saw this tour, so it is shown to them again.
 *
 * Behind `tour:reset_viewer` on the BFF, which only super admins hold, and scoped to the tour's
 * current revision — the same scope as every number on the reach modal.
 */
export const resetTourViewer = async (
  tourId: string,
  platformUserId: number,
): Promise<{ message: string }> => {
  const response = await authenticatedFetch(
    `${env.bffBaseURL}${TOURS_ENDPOINT}/${tourId}/viewers/${platformUserId}`,
    { method: 'DELETE' },
  );

  if (!response.ok) {
    const error: { message?: string } = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Não foi possível reexibir o tour para essa pessoa.');
  }

  return (await response.json()) as { message: string };
};
